import { Text } from "@ui/typography/Text";
import { For, Show } from "solid-js";
import "./ChipList.css";

export type ChipListItem = {
	alt: string;
	src?: string;
	initials?: string;
};

export type ChipListProps = {
	items: ChipListItem[];
	/** How many chips to show. Remaining items become “+N more”. */
	displayCount?: number;
	size?: "small" | "medium";
	class?: string;
};

export const chipInitials = (name: string) =>
	name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0])
		.join("")
		.toUpperCase();

/**
 * Overlapping circular chips for people or faces. Each chip shows an image
 * or initials. Set `displayCount` to cap visible chips and show “+N more”.
 *
 * [Design System →](http://localhost:5174/component/ChipList)
 */
export const ChipList = (props: ChipListProps) => {
	const size = () => props.size ?? "medium";
	const visible = () =>
		props.displayCount === undefined
			? props.items
			: props.items.slice(0, props.displayCount);
	const extra = () =>
		props.displayCount === undefined
			? 0
			: Math.max(0, props.items.length - props.displayCount);

	return (
		<span class={`gvtui-chip-list gvtui-chip-list--${size()} ${props.class ?? ""}`}>
			<span class="gvtui-chip-list__chips">
				<For each={visible()}>
					{(item) => (
						<span class="gvtui-chip-list__chip" title={item.alt}>
							<Show
								when={item.src}
								fallback={
									<span class="gvtui-chip-list__initials" aria-hidden="true">
										{item.initials ?? chipInitials(item.alt)}
									</span>
								}
							>
								<img
									class="gvtui-chip-list__image"
									src={item.src}
									alt={item.alt}
								/>
							</Show>
						</span>
					)}
				</For>
			</span>
			<Show when={extra() > 0}>
				<Text family="mono" size="xsmall" color="fg3">
					+{extra()} more
				</Text>
			</Show>
		</span>
	);
};
